import {
  DeleteOutlined,
  DownOutlined,
  ExportOutlined,
  GlobalOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SaveOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { Button, Form, Input, Modal, Switch, Tag, Tooltip } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { RichTextEditor } from '@/components/RichTextEditor/RichTextEditor';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useNotify } from '@/hooks/useNotify';
import {
  useDeleteCmsPageMutation,
  useGetCmsPageQuery,
  useGetCmsPagesQuery,
  useUpdateCmsPageMutation,
} from '@/modules/cms/cmsApi';
import {
  cmsPageLabel,
  cmsPublicPath,
  isSystemCmsSlug,
  type UpdateCmsPagePayload,
} from '@/types/cms';
import { API_URL } from '@/config/app';
import { resolveMutationResult } from '@/utils/apiResponse';
import { formatDateTime } from '@/utils/date';
import styles from './Cms.module.scss';

function storefrontOrigin(): string {
  try {
    const api = new URL(API_URL);
    if (api.hostname === 'localhost' || api.hostname === '127.0.0.1') {
      return 'http://localhost:3100';
    }
    if (api.hostname.startsWith('api.')) {
      return `${api.protocol}//${api.hostname.replace(/^api\./, '')}`;
    }
  } catch {
    /* ignore */
  }
  return '';
}

interface CmsFormValues {
  title: string;
  content: string;
  seoTitle: string;
  seoDescription: string;
  metaKeywords: string;
  isPublished: boolean;
}

export default function Cms() {
  const notify = useNotify();
  const [searchParams, setSearchParams] = useSearchParams();
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [seoCollapsed, setSeoCollapsed] = useState(true);

  const { data: pagesData, isLoading: isListLoading } = useGetCmsPagesQuery();
  const pages = pagesData?.results ?? [];

  const activeSlug = useMemo(() => {
    const fromQuery = searchParams.get('page')?.trim().toLowerCase() ?? '';
    if (fromQuery && pages.some((page) => page.slug === fromQuery)) {
      return fromQuery;
    }
    return pages[0]?.slug || 'about';
  }, [pages, searchParams]);

  const { data: page, isLoading } = useGetCmsPageQuery(activeSlug, { skip: !activeSlug });
  const [updateCmsPage, { isLoading: isSaving }] = useUpdateCmsPageMutation();
  const [deleteCmsPage, { isLoading: isDeleting }] = useDeleteCmsPageMutation();

  useGlobalLoader((isLoading || isListLoading) && !page && !pages.length);
  useGlobalLoader(isSaving || isDeleting);

  const { control, handleSubmit, reset, watch } = useForm<CmsFormValues>({
    defaultValues: {
      title: '',
      content: '',
      seoTitle: '',
      seoDescription: '',
      metaKeywords: '',
      isPublished: true,
    },
  });

  const isPublished = watch('isPublished');
  const seoTitle = watch('seoTitle');
  const seoDescription = watch('seoDescription');
  const metaKeywords = watch('metaKeywords');
  const hasSeoValues = Boolean(seoTitle || seoDescription || metaKeywords);
  const activeIsSystem = isSystemCmsSlug(activeSlug);
  const publicPath = cmsPublicPath(activeSlug);
  const publicHref = `${storefrontOrigin()}${publicPath}` || publicPath;

  const listedIsPublished = pages.find((item) => item.slug === activeSlug)?.isPublished ?? true;
  const isActivePageLoaded = page?.slug === activeSlug;

  useEffect(() => {
    const isCurrentPage = page?.slug === activeSlug;

    reset({
      title: isCurrentPage ? page.title || cmsPageLabel(activeSlug) : cmsPageLabel(activeSlug),
      content: isCurrentPage ? page.content : '',
      seoTitle: isCurrentPage ? page.seoTitle : '',
      seoDescription: isCurrentPage ? page.seoDescription : '',
      metaKeywords: isCurrentPage ? (page.metaKeywords ?? '') : '',
      isPublished: isCurrentPage ? page.isPublished : listedIsPublished,
    });
  }, [activeSlug, page, reset, listedIsPublished]);

  const setTab = (slug: string) => {
    setSearchParams({ page: slug });
  };

  const onSubmit = async (values: CmsFormValues) => {
    const payload: UpdateCmsPagePayload = {
      title: values.title.trim(),
      content: values.content,
      seoTitle: values.seoTitle.trim(),
      seoDescription: values.seoDescription.trim(),
      metaKeywords: values.metaKeywords.trim(),
      isPublished: values.isPublished,
    };

    const result = await resolveMutationResult(updateCmsPage({ slug: activeSlug, body: payload }));

    if (result.ok) {
      notify.success(
        values.isPublished
          ? 'CMS page saved and published on the website.'
          : 'CMS page saved as draft. It is hidden from the website.',
      );
      return;
    }

    notify.error(result.message);
  };

  const onDelete = () => {
    if (activeIsSystem) return;

    Modal.confirm({
      title: 'Delete this page?',
      content: `“${cmsPageLabel(activeSlug, page?.title)}” will be removed permanently. System pages cannot be deleted.`,
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        const result = await resolveMutationResult(deleteCmsPage(activeSlug));
        if (!result.ok) {
          notify.error(result.message);
          return;
        }
        notify.success(result.message ?? 'CMS page deleted.');
        setSearchParams({});
      },
    });
  };

  return (
    <div className={styles.page}>
      <PageHeader />

      <form
        onSubmit={handleSubmit(onSubmit)}
        className={styles.shell}
        data-nav-collapsed={navCollapsed ? 'true' : 'false'}
      >
        <aside className={`${styles.sidePanel} ${styles.pageNav}`} aria-label="CMS pages">
          <div className={styles.panelTop}>
            {!navCollapsed ? <span className={styles.panelLabel}>Pages</span> : null}
            <div className={styles.panelTopActions}>
              <button
                type="button"
                className={styles.collapseBtn}
                onClick={() => setNavCollapsed((value) => !value)}
                aria-label={navCollapsed ? 'Expand page menu' : 'Collapse page menu'}
              >
                {navCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              </button>
            </div>
          </div>

          <nav className={styles.navList}>
            {pages.map((item) => {
              const label = cmsPageLabel(item.slug, item.title);
              const isActive = activeSlug === item.slug;

              const button = (
                <button
                  type="button"
                  className={`${styles.navItem} ${isActive ? styles.navItemActive : ''}`}
                  onClick={() => setTab(item.slug)}
                >
                  {navCollapsed ? (
                    <span className={styles.navShort}>{label.charAt(0)}</span>
                  ) : (
                    <span className={styles.navItemRow}>
                      <span className={styles.navName}>{label}</span>
                      {!item.isPublished ? (
                        <span className={styles.draftBadge}>Draft</span>
                      ) : null}
                    </span>
                  )}
                </button>
              );

              if (navCollapsed) {
                return (
                  <Tooltip key={item.slug} title={label} placement="right">
                    {button}
                  </Tooltip>
                );
              }

              return (
                <span key={item.slug} className={styles.navItemWrap}>
                  {button}
                </span>
              );
            })}
          </nav>
        </aside>

        <section className={styles.editor}>
          <div className={styles.toolbar}>
            <div className={styles.toolbarMain}>
              <Form.Item className={styles.titleField}>
                <Controller
                  name="title"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      variant="borderless"
                      placeholder={cmsPageLabel(activeSlug)}
                    />
                  )}
                />
              </Form.Item>

              <div className={styles.toolbarMeta}>
                <Tag color={isPublished ? 'success' : 'default'} className={styles.statusTag}>
                  {isPublished ? 'Published' : 'Draft'}
                </Tag>
                {isActivePageLoaded && page?.updatedAt ? (
                  <span className={styles.updatedAt}>{formatDateTime(page.updatedAt)}</span>
                ) : null}
                <a
                  className={styles.viewLink}
                  href={publicHref}
                  target="_blank"
                  rel="noreferrer"
                  title="Open on website"
                >
                  <ExportOutlined /> View
                </a>
              </div>
            </div>

            <div className={styles.toolbarActions}>
              <div className={styles.publishToggle}>
                <span>Published</span>
                <Controller
                  name="isPublished"
                  control={control}
                  render={({ field }) => (
                    <Switch size="small" checked={field.value} onChange={field.onChange} />
                  )}
                />
              </div>
              {!activeIsSystem ? (
                <Button
                  danger
                  type="default"
                  icon={<DeleteOutlined />}
                  onClick={onDelete}
                  loading={isDeleting}
                >
                  Delete
                </Button>
              ) : null}
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={isSaving}>
                Save
              </Button>
            </div>
          </div>

          {!isPublished ? (
            <div className={styles.draftBanner} role="status">
              This page is a draft and is hidden from the website (header, footer and its URL).
              Turn on Published and save to show it.
            </div>
          ) : null}

          <div className={styles.contentArea}>
            <Controller
              name="content"
              control={control}
              render={({ field }) => (
                <RichTextEditor
                  key={activeSlug}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={`Write ${cmsPageLabel(activeSlug, page?.title).toLowerCase()} content...`}
                  height={520}
                  onUploadError={(message) => notify.error(message)}
                />
              )}
            />
          </div>

          <div className={styles.seoSection} data-collapsed={seoCollapsed ? 'true' : 'false'}>
            <button
              type="button"
              className={styles.seoHeader}
              onClick={() => setSeoCollapsed((value) => !value)}
              aria-expanded={!seoCollapsed}
            >
              <span className={styles.seoHeaderMain}>
                <GlobalOutlined />
                <span className={styles.panelLabel}>SEO</span>
                {seoCollapsed && hasSeoValues ? (
                  <span className={styles.seoFilledDot} aria-hidden />
                ) : null}
              </span>
              <span className={styles.seoChevron}>
                {seoCollapsed ? <DownOutlined /> : <UpOutlined />}
              </span>
            </button>

            {!seoCollapsed ? (
              <div className={styles.seoBody}>
                <p className={styles.seoHint}>
                  Search engine title, description, and keywords for this page. Public URL:{' '}
                  <code>{publicPath}</code>
                </p>

                <div className={styles.seoStack}>
                  <Form.Item label="SEO title" className={styles.field}>
                    <Controller
                      name="seoTitle"
                      control={control}
                      render={({ field }) => <Input {...field} placeholder="SEO title" />}
                    />
                  </Form.Item>

                  <Form.Item label="SEO description" className={styles.field}>
                    <Controller
                      name="seoDescription"
                      control={control}
                      render={({ field }) => (
                        <Input {...field} placeholder="Short description for search engines" />
                      )}
                    />
                  </Form.Item>

                  <Form.Item label="Meta keywords" className={styles.field}>
                    <Controller
                      name="metaKeywords"
                      control={control}
                      render={({ field }) => (
                        <Input {...field} placeholder="e.g. publications, factbook, elections" />
                      )}
                    />
                  </Form.Item>
                </div>

                {(seoTitle || seoDescription) && (
                  <div className={styles.seoPreview}>
                    <span className={styles.previewLabel}>Preview</span>
                    <div className={styles.previewTitle}>{seoTitle || 'Page title'}</div>
                    <div className={styles.previewDesc}>
                      {seoDescription || 'SEO description will appear here.'}
                    </div>
                    {metaKeywords ? (
                      <div className={styles.previewKeywords}>
                        <span className={styles.previewKeywordsLabel}>Keywords</span>
                        {metaKeywords}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </section>
      </form>
    </div>
  );
}
